import { Injectable, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ApolloQueryResult, FetchPolicy } from 'apollo-client';
import gql from 'graphql-tag';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import {
  AssessmentType,
  CreateQuestionnaireRequestInput,
  CreateQuestionnaireRequestMutation,
  CreateReminderMutation,
  GetQuestionnaireRequestQuery,
  IntakeForm,
  ListQuestionnaireRequestsByClinicIdSortedByCreatedAtQuery,
  MassClinicPatient,
  ModelSortDirection,
  QuestionnaireType,
  UpdateQuestionnaireRequestInput,
  UpdateQuestionnaireRequestMutation
} from 'src/API';
import {
  deq,
  dewsIIIQuestionnaire,
  fitzpatrick,
  medicationsQuestionnaire,
  osdi,
  osdi6,
  osdiAndSpeed,
  speed,
  speedii,
  standard_iplConsentForm,
  standard_iplConsentFollowUp,
  standard_consentForm
} from 'src/app/form-template/form-field-template-library';
import { ClinicSetupService } from 'src/app/logged-in-navbar/clinic-setup-modal/clinic-setup.service';
import { ReminderService } from 'src/app/shared/services/reminder.service';
import { safeParseJson } from 'src/app/shared/util/helper.util';
import { createQuestionnaireRequest, createReminder } from 'src/graphql/mutations';
import { listQuestionnaireRequestsByClinicIdSortedByCreatedAt } from 'src/graphql/queries';
import { AssessmentBody } from '../../econsult/assessment-body/assessment-body.model';
import { SymptomModes } from '../../shared/symptoms/symptom.model';
import { AppSyncService } from '../appsync.service';
import {
  ListQuestionnaireRequestsByGroupIdQuery,
  UpdateMassClinicPatientInput
} from './../../../API';
import { getQuestionnaireRequestWithResponse } from './../../../graphql/custom_queries';
import { Assessment, AssessmentService } from './assessment.service';
import { safeJsonParse } from './general.util';
import { Patient } from './patient.service';

export interface QuestionnaireWithStatus extends Partial<QuestionnaireRequest> {
  status: 'Completed' | 'Expired' | 'Incomplete';
}

export enum FitzpatrickType {
  type1 = 'Type I',
  type2 = 'Type II',
  type3 = 'Type III',
  type4 = 'Type IV',
  type5 = 'Type V',
  type6 = 'Type VI'
}

export enum QuestionnaireScoreType {
  FITZPATRICK = 'FITZPATRICK'
}

export type RiskDirection = 'above' | 'atOrAbove' | 'atOrBelow';

export interface InstrumentRiskThreshold {
  threshold: number;
  direction: RiskDirection;
}

export interface InstrumentScore {
  type: string;
  friendlyName: string;
  score: number;
  isAtRisk: boolean;
  category: string;
}

export type QuestionnaireRequest = CreateQuestionnaireRequestMutation['createQuestionnaireRequest'];
export type ReminderRequest = CreateReminderMutation['createReminder'];

export interface SendQuestionnaireDialogData {
  patient: Partial<Patient>;
  questionnaireType: QuestionnaireType;
  massClinicPatient?: MassClinicPatient;
  questionnaireRequest?: QuestionnaireRequest;
  multipleQuestionnairesSelectable?: boolean;
  isSideSheet?: boolean;
  /** Picker keys ticked when the multi-select picker opens (e.g. resending a declined consent). */
  preselectedQuestionnaireKeys?: string[];
}

export interface TabletQuestionnaireModalData {
  patient: any; // Partial<Patient> not working for some reason
  questionnaireType: QuestionnaireType;
  questionnaireRequest?: QuestionnaireRequest;
}

export interface ReminderConfig {
  body: {
    textType: 'Html';
    message: string;
  };
  [key: string]: unknown;
}
export type ReminderSchedule = {
  disabled: boolean;
  friendlyName: string;
  name: string;
};
export const ONE_DAY_IN_MILLISECONDS: number = 86400000;
export const DEFAULT_QUESTIONNAIRE_EXPIRY_DAYS = 14;

@Injectable({
  providedIn: 'root'
})
export class QuestionnaireRequestService implements OnDestroy {
  private static SendQuestionnaireValidator(group: FormGroup): ValidationErrors {
    return !!group.value.mobile || !!group.value.email
      ? null
      : { noDeliveryMethod: 'Please select a delivery method.' };
  }

  public QuestionnaireType = QuestionnaireType;

  public questionnairesWithStatus: QuestionnaireWithStatus[];

  public formTemplateLibraryQuestionnaires: { [key in QuestionnaireType]?: any } = {
    [QuestionnaireType.FITZPATRICK]: fitzpatrick,
    [QuestionnaireType.OSDI]: osdi,
    [QuestionnaireType.OSDI6]: osdi6,
    [QuestionnaireType.DEQ]: deq,
    [QuestionnaireType.SPEED]: speed,
    [QuestionnaireType.SPEEDII]: speedii,
    [QuestionnaireType.MEDICATION]: medicationsQuestionnaire,
    [QuestionnaireType.OSDIAndSPEED]: osdiAndSpeed,
    [QuestionnaireType.DEWSIII_Q]: dewsIIIQuestionnaire
  };

  public questionnaireTypeToFriendlyNameMap: { [key in QuestionnaireType]?: string } = {
    [QuestionnaireType.DEQ]: 'DEQ',
    [QuestionnaireType.OSDI]: 'OSDI 12',
    [QuestionnaireType.SPEED]: 'SPEED',
    [QuestionnaireType.SPEEDII]: 'SPEED II',
    [QuestionnaireType.OSDIAndSPEED]: 'OSDI 12 and SPEED',
    [QuestionnaireType.DERFS]: 'DERFS',
    [QuestionnaireType.CDERFS]: 'CDERFS',
    [QuestionnaireType.FITZPATRICK]: 'Fitzpatrick',
    [QuestionnaireType.OSDI6]: 'OSDI 6',
    [QuestionnaireType.CUSTOM]: 'Custom',
    [QuestionnaireType.MEDICATION]: 'Medications',
    [QuestionnaireType.DEWSIII_Q]: 'DEWS III'
  };

  public readonly instrumentRiskThresholds: {
    [key in QuestionnaireType]?: InstrumentRiskThreshold
  } = {
    [QuestionnaireType.DEQ]: { threshold: 5, direction: 'above' },
    [QuestionnaireType.OSDI]: { threshold: 13, direction: 'atOrAbove' },
    [QuestionnaireType.SPEED]: { threshold: 5, direction: 'atOrAbove' },
    [QuestionnaireType.SPEEDII]: { threshold: 5, direction: 'atOrAbove' },
    [QuestionnaireType.OSDI6]: { threshold: 4, direction: 'atOrAbove' },
    [QuestionnaireType.FITZPATRICK]: { threshold: 25, direction: 'atOrBelow' }
  };

  public readonly unscoredQuestionnaireTypes: string[] = [
    QuestionnaireType.DERFS,
    QuestionnaireType.CDERFS
  ];

  public isUnscoredQuestionnaire(questionnaireType: string): boolean {
    return this.unscoredQuestionnaireTypes.indexOf(questionnaireType) > -1;
  }

  public readonly questionnaireHeaderMap = {
    createdAt: 'Sent On',
    type: 'Type',
    completedAt: 'Completed At',
    status: 'Status',
    score: 'Score',
    assessment: 'Linked Assessment Date',
    source: 'Submitted From'
  };

  public readonly questionnaireHeaderKeys = Object.keys(this.questionnaireHeaderMap);

  private twoWeeksBack = this.getDaysBackInMilliSeconds(14);
  public readonly questionnaireHeaderKeysWithActionsKey = [
    ...this.questionnaireHeaderKeys,
    'actions'
  ];

  private readonly questionnaireRequestPublicFields = `
    fragment QuestionnaireRequestPublicFields on QuestionnaireRequest {
      id
      type
      completedAt
      country
      consentsToResearch
      consentsToPrivacyForm
      consentSource
      createdAt
      response {
        answers
        scores
      }
      nonModifiableData
      language
      config
    }
  `;

  private readonly getQuestionnaireRequestLimited = `
    query GetQuestionnaireRequest($id: ID!) {
      getQuestionnaireRequest(id: $id) {
        ...QuestionnaireRequestPublicFields
      }
    }
    ${this.questionnaireRequestPublicFields}
  `;

  private readonly updateQuestionnaireRequestLimited = `
    mutation UpdateQuestionnaireRequest($input: UpdateQuestionnaireRequestInput!) {
      updateQuestionnaireRequest(input: $input) {
        ...QuestionnaireRequestPublicFields
      }
    }
    ${this.questionnaireRequestPublicFields}
  `;

  private listQuestionnaireRequestsByGroupIdLimited = /* GraphQL */ `
    query ListQuestionnaireRequestsByGroupId(
      $groupId: ID!
      $sortDirection: ModelSortDirection
      $limit: Int
      $nextToken: String
    ) {
      listQuestionnaireRequestsByGroupId(
        groupId: $groupId
        sortDirection: $sortDirection
        limit: $limit
        nextToken: $nextToken
      ) {
        items {
          ...QuestionnaireRequestPublicFields
        }
        nextToken
      }
    }
    ${this.questionnaireRequestPublicFields}
  `;

  private createQuestionnaireRequestAndUpdateMassClinicPatientMutation = /* GraphQL */ `
    mutation UpdateMassClinicPatientAfterQuestionnaireRequestCreation(
      $createQuestionnaireRequestInput: CreateQuestionnaireRequestInput!
      $updateMassClinicPatientInput: UpdateMassClinicPatientInput!
    ) {
      createQuestionnaireRequest(input: $createQuestionnaireRequestInput) {
        id
        createdAt
        type
      }
      updateMassClinicPatient(input: $updateMassClinicPatientInput) {
        id
        questionnaireRequest {
          id
        }
      }
    }
  `;

  private updateQuestionnaireRequestMutation = /* GraphQL */ `
    mutation UpdateQuestionnaireRequest($input: UpdateQuestionnaireRequestInput!) {
      updateQuestionnaireRequest(input: $input) {
        id
        email
        mobile
        assessment {
          id
          createdAt
          updatedAt
        }
        questionnaireRequestPatientId
        type
        config
        response {
          answers
          scores
          consent {
            consentsToPrivacyForm
            consentsToResearch
            agreedToTerms
            consentSource
          }
        }
        country
        consentsToPrivacyForm
        consentSource
        consentsToResearch
        completedAt
        createdAt
        updatedAt
      }
    }
  `;

  public customQuestionnaireKeyToSchemaMap: { [key: string]: any } = {};
  public customQuestionnaireKeyToFriendlyNameMap: { [key: string]: any } = {};
  public hasCustomQuestionnaires = false;
  public consentFormKeyToSchemaMap: { [key: string]: { name?: string; type?: string } } = {};
  public consentFormKeyToFriendlyNameMap: { [key: string]: string } = {};

  public customIntakeFormKeyToSchemaMap: { [key: string]: any } = {};
  public customIntakeFormKeyToFriendlyNameMap: { [key: string]: any } = {};

  public defaultConsentFormKeyToSchemaMap: { [key: string]: any } = {
      standard_consentForm,
      standard_iplConsentForm,
      standard_iplConsentFollowUp
    };
  public defaultConsentFormKeyToFriendlyNameMap: { [key: string]: any } = {
      standard_consentForm: standard_consentForm.name,
      standard_iplConsentForm: standard_iplConsentForm.name,
      standard_iplConsentFollowUp: standard_iplConsentFollowUp.name
    };

  public allQuestionnairesAndIntakeFormsKeyToSchemaMap: { [key: string]: any } = {
      ...this.formTemplateLibraryQuestionnaires,
      ...this.defaultConsentFormKeyToSchemaMap
    };

  constructor(
    private appSyncService: AppSyncService,
    private formBuilder: FormBuilder,
    private reminderService: ReminderService,
    private assessmentService: AssessmentService,
    private clinicSetupService: ClinicSetupService
  ) {
    this.clinicSetupService.formTemplates$.pipe(untilDestroyed(this)).subscribe(formTemplates => {
      formTemplates.forEach(formTemplate => {
        if (formTemplate.schema.type === 'intake') {
          this.customIntakeFormKeyToSchemaMap[formTemplate.id] = formTemplate.schema;
          this.customIntakeFormKeyToFriendlyNameMap[formTemplate.id] = formTemplate.schema.name;
        } else if (formTemplate.schema.type === 'questionnaire') {
          this.hasCustomQuestionnaires = true;
          this.customQuestionnaireKeyToSchemaMap[formTemplate.id] = formTemplate.schema;
          this.customQuestionnaireKeyToFriendlyNameMap[formTemplate.id] = formTemplate.schema.name;
        } else if (formTemplate.schema.type === 'consent') {
          this.consentFormKeyToSchemaMap[formTemplate.id] = formTemplate.schema;
          this.consentFormKeyToFriendlyNameMap[formTemplate.id] = formTemplate.schema.name;
        }
      });
      this.allQuestionnairesAndIntakeFormsKeyToSchemaMap = {
        ...this.allQuestionnairesAndIntakeFormsKeyToSchemaMap,
        ...this.customIntakeFormKeyToSchemaMap,
        ...this.customQuestionnaireKeyToSchemaMap,
        ...this.defaultConsentFormKeyToSchemaMap,
        ...this.consentFormKeyToSchemaMap
      };
    });
  }

  public createFormGroup(data: SendQuestionnaireDialogData) {
    return this.formBuilder.group(
      {
        email: [data.patient.email, [Validators.email, Validators.required]],
        mobile: [data.patient.phone, Validators.required],
        type: data.multipleQuestionnairesSelectable
          ? data.questionnaireType
          : [data.questionnaireType, Validators.required],
        questionnaireRequestPatientId: data.patient.id,
        country: data.patient.clinic.country,
        consentsToPrivacyForm: data.patient.consentsToPrivacyForm,
        consentSource: data.patient.consentSource,
        consentsToResearch: data.patient.consentsToResearch,
        nonModifiableData: data.patient.nonModifiableData
      },
      { validators: QuestionnaireRequestService.SendQuestionnaireValidator }
    );
  }

  public createFormGroupForQuestionnaireTypes() {
    return this.formBuilder.group({
      ...this.questionnaireHeaderKeys.map(key => {
        return { [key]: null };
      })
    });
  }

  public createTabletQuestionnaireFormGroup(data: TabletQuestionnaireModalData) {
    return this.formBuilder.group(
      {
        type: [data.questionnaireType, Validators.required],
        questionnaireRequestPatientId: data.patient.id,
        country: data.patient.clinic.country,
        consentsToPrivacyForm: data.patient.consentsToPrivacyForm,
        consentSource: data.patient.consentSource,
        consentsToResearch: data.patient.consentsToResearch
      },
      {}
    );
  }

  public createQuestionnaireRequest(
    input: CreateQuestionnaireRequestInput
  ): Observable<QuestionnaireRequest> {
    return this.appSyncService.hydrated().pipe(
      switchMap(client =>
        client.mutate({
          mutation: gql(createQuestionnaireRequest),
          variables: { input: input }
        })
      ),
      map(
        (result: ApolloQueryResult<CreateQuestionnaireRequestMutation>) =>
          result.data.createQuestionnaireRequest
      ),
      tap(questionnaire => this.reminderService.createReminder(questionnaire).subscribe())
    );
  }

  public createQuestionnaireRequestAndUpdateMassClinicPatient(
    createQuestionnaireRequestInput: CreateQuestionnaireRequestInput,
    updateMassClinicPatientInput: UpdateMassClinicPatientInput
  ): Observable<QuestionnaireRequest> {
    return this.appSyncService.hydrated().pipe(
      switchMap(client =>
        client.mutate({
          mutation: gql(this.createQuestionnaireRequestAndUpdateMassClinicPatientMutation),
          variables: { createQuestionnaireRequestInput, updateMassClinicPatientInput }
        })
      ),
      map(
        (
          result: ApolloQueryResult<{
            createQuestionnaireRequest: QuestionnaireRequest;
            UpdateMassClinicPatientInput: MassClinicPatient;
          }>
        ) => result.data.createQuestionnaireRequest
      )
    );
  }

  public createQuestionnaireReminder(
    createReminderRequestInput: Partial<ReminderRequest>
  ): Observable<ReminderRequest> {
    return this.appSyncService.hydrated().pipe(
      switchMap(client =>
        client.mutate({
          mutation: gql(createReminder),
          variables: { input: createReminderRequestInput }
        })
      ),
      map((result: ApolloQueryResult<CreateReminderMutation>) => result.data.createReminder)
    );
  }

  public createAssessmentAndSendQuestionnaireRequest(
    questionnaireRequestInput: CreateQuestionnaireRequestInput,
    patientId: string
  ): Observable<QuestionnaireRequest> {
    return this.createAssessmentForSendingQuestionnaire(questionnaireRequestInput.type, patientId)
      .pipe(
        tap(
          (assessment: Assessment) =>
            (questionnaireRequestInput.questionnaireRequestAssessmentId = assessment.id)
        )
      )
      .pipe(switchMap(() => this.createQuestionnaireRequest(questionnaireRequestInput)));
  }

  public getQuestionnaireRequest(
    id: string,
    limitToPatientView = true
  ): Observable<QuestionnaireRequest> {
    const query = limitToPatientView
      ? this.getQuestionnaireRequestLimited
      : getQuestionnaireRequestWithResponse;

    return this.appSyncService.hydrated(limitToPatientView).pipe(
      switchMap(client =>
        client.query({
          query: gql(query),
          variables: { id: id }
        })
      ),
      map(
        (result: ApolloQueryResult<GetQuestionnaireRequestQuery>) =>
          result.data.getQuestionnaireRequest
      )
    );
  }

  getQuestionnaireFriendlyName(questionnaireRequest: any): string {
    const questionnaireType = questionnaireRequest.type;
    if (questionnaireType === QuestionnaireType.CUSTOM) {
      const parsedConfig =
        typeof questionnaireRequest.config === 'object' && questionnaireRequest.config !== null
          ? questionnaireRequest.config
          : safeJsonParse(questionnaireRequest.config);
      if (parsedConfig && parsedConfig.schema && parsedConfig.schema.name) {
        return parsedConfig.schema.name;
      }
    }
    return this.questionnaireTypeToFriendlyNameMap[questionnaireType] || questionnaireType;
  }

  public getQuestionnaireRequestsCreatedBetweenDateRange(
    questionnaireRequestClinicId: string,
    dateRange: [string, string],
    fetchPolicy: FetchPolicy = 'cache-first'
  ): Observable<Partial<QuestionnaireRequest[]>> {
    return this.appSyncService.hydrated().pipe(
      switchMap(client =>
        client.query({
          query: gql(listQuestionnaireRequestsByClinicIdSortedByCreatedAt),
          variables: {
            questionnaireRequestClinicId,
            sortDirection: ModelSortDirection.DESC,
            createdAt: { between: dateRange }
          },
          fetchPolicy
        })
      ),

      map(
        (result: ApolloQueryResult<ListQuestionnaireRequestsByClinicIdSortedByCreatedAtQuery>) =>
          result.data.listQuestionnaireRequestsByClinicIdSortedByCreatedAt
            .items as QuestionnaireRequest[]
      )
    );
  }

  public updateQuestionnaireType(
    questionnaireRequest: QuestionnaireRequest,
    type: QuestionnaireType
  ): Observable<QuestionnaireRequest> {
    const {
      id,
      questionnaireRequestPatientId,
      country,
      email,
      mobile,
      consentsToPrivacyForm,
      consentSource,
      consentsToResearch
    } = questionnaireRequest;
    const updateQuestionnaireRequestInput: UpdateQuestionnaireRequestInput = {
      id,
      questionnaireRequestPatientId,
      country,
      type,
      email,
      mobile,
      consentsToPrivacyForm,
      consentSource,
      consentsToResearch
    };

    if (questionnaireRequest.massClinicPatient) {
      updateQuestionnaireRequestInput.questionnaireRequestMassClinicPatientId =
        questionnaireRequest.massClinicPatient.id;
    }

    return this.updateQuestionnaireRequest(updateQuestionnaireRequestInput);
  }

  public updateQuestionnaireRequest(
    input: UpdateQuestionnaireRequestInput,
    limitToPatientView = true
  ): Observable<QuestionnaireRequest> {
    const mutation = limitToPatientView
      ? this.updateQuestionnaireRequestLimited
      : this.updateQuestionnaireRequestMutation;
    return this.appSyncService.hydrated(limitToPatientView).pipe(
      switchMap(client =>
        client.mutate({
          mutation: gql(mutation),
          variables: { input: input }
        })
      ),
      map(
        (result: ApolloQueryResult<UpdateQuestionnaireRequestMutation>) =>
          result.data.updateQuestionnaireRequest
      )
    );
  }

  private createAssessmentForSendingQuestionnaire(
    questionnaireType: QuestionnaireType,
    patientId: string
  ): Observable<Assessment> {
    const defaultNewAssessmentType: AssessmentType = AssessmentType.DryEyeSpecialized;
    const body: Partial<AssessmentBody> = {
      assessmentType: defaultNewAssessmentType,
      dryEyeForm: {
        assessmentMethod: this.getSymptomModeForQuestionnaireType(questionnaireType)
      }
    };
    return this.assessmentService.createAssessment(defaultNewAssessmentType, body, null, patientId);
  }

  private getSymptomModeForQuestionnaireType(type: QuestionnaireType) {
    switch (type) {
      case QuestionnaireType.DEQ:
        return SymptomModes.Simple;
      default:
        return SymptomModes.Advanced;
    }
  }

  // Every questionnaire carries the expiry it was sent with, so changing the clinic setting later
  // cannot re-date a link that is already out. The send day is not counted against the patient.
  public isQuestionnaireExpired(questionnaire: {
    completedAt?: string | null;
    createdAt?: string | null;
    config?: string | null;
  }): boolean {
    if (!questionnaire || questionnaire.completedAt || !questionnaire.createdAt) {
      return false;
    }
    const createdDate = new Date(questionnaire.createdAt);
    if (isNaN(createdDate.getTime())) {
      return false;
    }
    const questionnaireConfig = safeParseJson(questionnaire.config, {}) || {};
    const daysValidUntil =
      questionnaireConfig.questionnaireExpiry || DEFAULT_QUESTIONNAIRE_EXPIRY_DAYS;
    const endOfCreatedDay = new Date(createdDate.getTime()).setHours(24, 0, 0, 0);

    return new Date().getTime() - endOfCreatedDay > daysValidUntil * ONE_DAY_IN_MILLISECONDS;
  }

  public setQuestionnaireStatus(
    questionnaire: Partial<QuestionnaireRequest>
  ): QuestionnaireWithStatus {
    if (questionnaire.completedAt) {
      return { ...questionnaire, status: 'Completed' };
    }
    if (this.isQuestionnaireExpired(questionnaire)) {
      return { ...questionnaire, status: 'Expired' };
    }
    return { ...questionnaire, status: 'Incomplete' };
  }

  getDaysBackInMilliSeconds(days: number): number {
    const daysInMilliSeconds = days * ONE_DAY_IN_MILLISECONDS;

    const dateToday = new Date();
    dateToday.setHours(0);
    dateToday.setMinutes(0);
    dateToday.setMilliseconds(0);

    return dateToday.getTime() - daysInMilliSeconds;
  }

  public customSpeedThreshold(currentScore: string): boolean {
    const scoreConfigs =
      this.clinicSetupService.questionnaireScoreConfig.SPEED &&
      this.clinicSetupService.questionnaireScoreConfig.SPEED.scoreConfig;

    if (!scoreConfigs) return null;

    for (const config of scoreConfigs) {
      const { score, comparator, message } = config;
      if (this.compare(currentScore, comparator, score)) return true;
    }

    return null;
  }

  public compare(currentScore: string, comparator: string, score: number) {
    switch (comparator) {
      case '=':
        return parseInt(currentScore) === score;
      case '>':
        return parseInt(currentScore) > score;

      case '<':
        return parseInt(currentScore) < score;
    }
  }

  public isAtRisk(scores: string): boolean {
    const scoreMap = safeJsonParse(scores);
    if (!scoreMap) {
      return false;
    }
    return Object.keys(scoreMap).some(scoreType =>
      this.isInstrumentAtRisk(scoreType, Number(scoreMap[scoreType]))
    );
  }

  public isInstrumentAtRisk(questionnaireType: string, score: number): boolean {
    const riskThreshold = this.instrumentRiskThresholds[questionnaireType];
    if (!riskThreshold || score === null || score === undefined || isNaN(score)) {
      return false;
    }
    if (riskThreshold.direction === 'above') {
      return score > riskThreshold.threshold;
    }
    if (riskThreshold.direction === 'atOrAbove') {
      return score >= riskThreshold.threshold;
    }
    return score <= riskThreshold.threshold;
  }

  public hasCategorizedScore(
    questionnaire: Partial<QuestionnaireRequest> | Partial<IntakeForm>
  ): boolean {
    if (!(questionnaire && questionnaire.response && questionnaire.response['scores'])) {
      return false;
    }

    if (this.isUnscoredQuestionnaire(questionnaire['type'])) {
      return false;
    }

    const scoreMap = safeJsonParse(questionnaire.response['scores']);
    return !!scoreMap && Object.keys(scoreMap).length > 0 && scoreMap['CUSTOM'] !== 'N/A';
  }

  public getInstrumentScores(
    questionnaire: Partial<QuestionnaireRequest> | Partial<IntakeForm>
  ): InstrumentScore[] {
    if (!this.hasCategorizedScore(questionnaire)) {
      return [];
    }

    const scoreMap = safeJsonParse(questionnaire.response['scores']);
    return Object.keys(scoreMap).map(scoreType => {
      const score = Number(scoreMap[scoreType]);
      return {
        type: scoreType,
        friendlyName: this.questionnaireTypeToFriendlyNameMap[scoreType] || scoreType,
        score: score,
        isAtRisk: this.isInstrumentAtRisk(scoreType, score),
        category: this.getCategorizedScore(scoreType, score)
      };
    });
  }

  public getCategorizedScore(questionnaireType: string, score: number): string {
    let category = '';
    switch (questionnaireType) {
      case QuestionnaireScoreType.FITZPATRICK: {
        category = this.getFitzpatrickType(score);
        break;
      }
    }
    return category;
  }

  public getFitzpatrickType(fitzpatrickScore: number) {
    if (fitzpatrickScore <= 7) {
      return FitzpatrickType.type1;
    } else if (fitzpatrickScore <= 16) {
      return FitzpatrickType.type2;
    } else if (fitzpatrickScore <= 25) {
      return FitzpatrickType.type3;
    } else if (fitzpatrickScore <= 30) {
      return FitzpatrickType.type4;
    } else if (fitzpatrickScore <= 36) {
      return FitzpatrickType.type5;
    } else if (fitzpatrickScore <= 40) {
      return FitzpatrickType.type6;
    }
    return '';
  }

  public getLastCompletedLinkedQuestionnaireByType(
    linkedQuestionnaires: any[],
    lastCompletedLinkedQuestionnaireByTypeMap: any = {}
  ) {
    let linkedQuestionnairesByType: {
      [key: string]: QuestionnaireRequest[];
    } = this.getQuestionnaireTypeToQuestionnairesMap(linkedQuestionnaires);

    lastCompletedLinkedQuestionnaireByTypeMap = Object.keys(linkedQuestionnairesByType).reduce(
      (acc, questionnaireType) => {
        const lastCompletedQuestionnaire = this.getLastCompletedQuestionnaire(
          linkedQuestionnairesByType[questionnaireType]
        );
        if (lastCompletedQuestionnaire) {
          acc[questionnaireType] = lastCompletedQuestionnaire;
        } else {
          acc[questionnaireType] = lastCompletedLinkedQuestionnaireByTypeMap[questionnaireType];
        }
        return acc;
      },
      {}
    );
    return lastCompletedLinkedQuestionnaireByTypeMap;
  }

  public getLastCompletedQuestionnairesByScore(
    questionnaires: any[],
    lastCompletedQuestionnaireByScoreMap: any = {}
  ) {
    let questionnaireScoreTypeToQuestionnairesMap: {
      [key: string]: QuestionnaireRequest[];
    } = this.getQuestionnaireScoreTypeToQuestionnairesMap(questionnaires);
    let newLastCompletedQuestionnaireByScoreMap = Object.keys(
      questionnaireScoreTypeToQuestionnairesMap
    ).reduce((acc, key) => {
      if (lastCompletedQuestionnaireByScoreMap[key]) {
        questionnaireScoreTypeToQuestionnairesMap[key].push(
          lastCompletedQuestionnaireByScoreMap[key]
        );
      }
      const lastCompletedQuestionnaire = this.getLastCompletedQuestionnaire(
        questionnaireScoreTypeToQuestionnairesMap[key]
      );
      if (lastCompletedQuestionnaire) {
        acc[key] = lastCompletedQuestionnaire;
      } else {
        acc[key] = lastCompletedQuestionnaireByScoreMap[key];
      }
      return acc;
    }, {});
    return { ...lastCompletedQuestionnaireByScoreMap, ...newLastCompletedQuestionnaireByScoreMap };
  }

  public linkQuestionnaireToAssessment(
    questionnaire: QuestionnaireRequest,
    assessmentData: Assessment
  ) {
    this.updateQuestionnaireRequest(
      {
        id: questionnaire.id,
        questionnaireRequestAssessmentId: assessmentData.id
      },
      false
    ).subscribe(questionnaireResult => {
      assessmentData.questionnaireRequests = {
        ...assessmentData.questionnaireRequests,
        items: assessmentData.questionnaireRequests
          ? [...assessmentData.questionnaireRequests.items, questionnaireResult]
          : [questionnaireResult]
      };
      questionnaire.assessment = assessmentData;
    });
    return { ...questionnaire };
  }

  public getQuestionnaireScoreTypeToQuestionnairesMap(questionnaires: QuestionnaireRequest[]) {
    let questionnaireScoreTypeToQuestionnairesMap: {
      [key: string]: QuestionnaireRequest[];
    } = {};
    for (let questionnaire of questionnaires) {
      const scores = JSON.parse(questionnaire.response.scores);
      Object.keys(scores).forEach(key => {
        questionnaireScoreTypeToQuestionnairesMap[key]
          ? questionnaireScoreTypeToQuestionnairesMap[key].push(questionnaire)
          : (questionnaireScoreTypeToQuestionnairesMap[key] = [questionnaire]);
      });
    }
    return questionnaireScoreTypeToQuestionnairesMap;
  }

  public getLastCompletedQuestionnaire(questionnaires: any[]) {
    if (!questionnaires) return null;
    const mostRecentDate = new Date(
      Math.max.apply(
        null,
        questionnaires.map(item => {
          return new Date(item.completedAt);
        })
      )
    );
    const mostRecentObject = questionnaires.filter(item => {
      const d = new Date(item.completedAt);
      return d.getTime() === mostRecentDate.getTime();
    })[0];
    return mostRecentObject;
  }

  public getQuestionnaireTypeToQuestionnairesMap(questionnaires: any[]) {
    let questionnaireTypeToQuestionnairesMap = {};
    for (let questionnaire of questionnaires) {
      let questionnaireType = this.getQuestionnaireType(questionnaire);
      questionnaireTypeToQuestionnairesMap[questionnaireType]
        ? questionnaireTypeToQuestionnairesMap[questionnaireType].push(questionnaire)
        : (questionnaireTypeToQuestionnairesMap[questionnaireType] = [questionnaire]);
    }
    return questionnaireTypeToQuestionnairesMap;
  }

  public getQuestionnaireType(questionnaire: any) {
    if (questionnaire.type === QuestionnaireType.CUSTOM) {
      try {
        return (questionnaire.config as any) instanceof Object
          ? questionnaire.config['schema']['abbreviation']
          : JSON.parse(questionnaire.config).schema.abbreviation;
      } catch {
        return QuestionnaireType.CUSTOM;
      }
    }
    return questionnaire.type;
  }

  public listQuestionnaireRequestsByGroupId(groupId): Observable<QuestionnaireRequest[]> {
    return this.appSyncService.hydrated(true).pipe(
      switchMap(client =>
        client.query({
          query: gql(this.listQuestionnaireRequestsByGroupIdLimited),
          variables: {
            groupId
          }
        })
      ),
      map(
        (result: ApolloQueryResult<ListQuestionnaireRequestsByGroupIdQuery>) =>
          result.data.listQuestionnaireRequestsByGroupId.items as QuestionnaireRequest[]
      )
    );
  }

  public ngOnDestroy(): void {}
}
